import { EventWithDetail, OrgFullDetail } from "@garther/interfaces";
import { Avatar, Box, Button, Input, Progress, TiptapView } from "@garther/ui/components";
import { refLink, toast, useI18n } from "@garther/ui/libs";
import { useNavigate } from "@tanstack/react-router";
import { Copy } from "lucide-react";
import { useMemo } from "react";

import { AppCfg } from "@/shared/config/app";

interface EventInfoProps {
  org: OrgFullDetail;
  event: EventWithDetail;
}

export const EventInfo = ({ org, event }: EventInfoProps) => {
  const { t, formatCredit } = useI18n();

  const navigate = useNavigate();

  const referralLink = useMemo(() => {
    if (org.account?.referralCode)
      return refLink(`${AppCfg.clientBaseUrl}/event/${event.id}`, org.account?.referralCode);
    return "";
  }, [org.account?.referralCode, event.id]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success(t("credits.wallet.referralLinkCopied"));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="space-y-2">
          <Box className="space-y-4" label={t("eventDetail.aboutThisEvent")}>
            <TiptapView content={event.description} variant="normal" />
            <div className="space-y-2 bg-background-highlight rounded-sm p-4">
              <h4 className="text-responsive-sm font-medium text-text-secondary">
                {t("eventDetail.hostedBy")}
              </h4>
              <div
                className="flex items-center gap-4 w-fit transition-all duration-300 transform hover:scale-[1.04] cursor-pointer"
                onClick={() =>
                  navigate({
                    to: "/org/$orgId",
                    params: { orgId: event.host.slug ?? event.host.id }
                  })
                }
              >
                <Avatar
                  name={event.host.name}
                  src={event.host.avatar || undefined}
                  size="lg"
                  className="h-10 w-10 flex-shrink-0"
                />
                <div>
                  <p className="text-text-primary text-responsive-md font-normal">{event.host.name}</p>
                  <p className="text-text-secondary text-responsive-sm font-medium">{event.host.tagline}</p>
                </div>
              </div>
            </div>
          </Box>
        </div>
      </div>

      {/* Right Column - Sidebar */}
      <div className="lg:col-span-1">
        <div className="sticky top-24 space-y-2">
          {/* <Box label={t("credits.wallet.yourReferralLink")}> */}
          {/*   <SelectableCard className="w-full" onClick={handleCopyLink}> */}
          {/*     Web */}
          {/*   </SelectableCard> */}
          {/*   <SelectableCard className="w-full" onClick={handleCopyLink}> */}
          {/*     Zalo Mini App */}
          {/*   </SelectableCard> */}
          {/* </Box> */}
          {/* Referral Link */}
          <div className="space-y-2">
            <p className="text-responsive-md font-light text-text-secondary font-literata uppercase pl-4 md:pl-6 mb-2 tracking-[0.1em]">
              {t("credits.wallet.yourReferralLink")}
            </p>
            <div className="flex gap-2 w-full">
              <Input
                containerClassName="flex-1"
                value={referralLink}
                readOnly
                className="bg-background-secondary border-border text-text-primary text-responsive-base"
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="border-border text-text-primary hover:bg-background-highlight shrink-0"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Box label="THỐNG KÊ">
            <div className="space-y-4">
              <div>
                <p className="text-responsive-sm font-medium text-text-secondary">Total registered</p>
                <p className="text-responsive-base font-normal text-text-primary">
                  {event.stats.ticketsSold}
                  <span className="text-responsive-sm font-medium text-text-secondary">
                    /{event.stats.ticketsCap}
                  </span>
                </p>
              </div>
              {event.sessions[0]?.ticketTiers.map((t, i) => (
                <div key={i} className="ml-3">
                  <p className="text-responsive-sm font-medium text-text-secondary">{t.title}</p>
                  <Progress value={(t.taken / t.cap) * 100} />
                  <p className="text-responsive-base font-normal text-text-primary">
                    {t.taken}
                    <span className="text-responsive-sm font-medium text-text-secondary">/{t.cap}</span>
                  </p>
                </div>
              ))}
              <div>
                <p className="text-responsive-sm font-medium text-text-secondary">Total Revenue (Gross)</p>
                <p className="text-responsive-base font-normal text-text-primary">
                  {formatCredit(event.stats.revenueGross)}
                </p>
              </div>
              <div>
                <p className="text-responsive-sm font-medium text-text-secondary">Total Revenue (Net)</p>
                <p className="text-responsive-base font-normal text-text-primary">
                  {formatCredit(event.stats.revenueNet)}
                </p>
              </div>
              <div>
                <p className="text-responsive-sm font-medium text-text-secondary">Bookings</p>
                <p className="text-responsive-base font-normal text-text-primary">
                  <span className="text-green-600">{event.stats.bookingsPaid} paid</span>
                  {" · "}
                  <span className="text-red-600">{event.stats.bookingsCancelled} cancelled</span>
                  {" · "}
                  <span className="text-text-secondary">{event.stats.bookingsTotal} total</span>
                </p>
              </div>
            </div>
          </Box>
        </div>
      </div>
    </div>
  );
};
